import React, { useState } from "react";
import {
  Box, Typography, TextField, MenuItem, FormControlLabel, Switch, Button,
} from "@mui/material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import {
  WebsiteConfig, readWebsiteConfig, REFRESH_INTERVALS, WEBSITE_FITS,
  validateWebsiteInput, extractIframeSrc, extractIframeMeta, isValidWebUrl,
} from "./websiteConfig";

/**
 * Embed / iFrame zone inspector — advanced external embedded content that is
 * NOT a normal website or map. Accepts either a URL or pasted <iframe> embed
 * code; in both cases only the safe http(s) `src` is ever stored (scripts,
 * srcdoc and event handlers are stripped by extractIframeSrc — the same rule
 * the backend re-applies). Raw HTML is never persisted or rendered.
 *
 * It intentionally does NOT rewrite `display_mode`: new embed zones are created
 * as `iframe_embed`, and legacy embedded/direct-video zones opened here keep
 * their stored mode so they render exactly as before.
 */
interface Props {
  cfg: Record<string, any>;
  setCfg: (patch: Record<string, any>) => void;
  interacting: boolean;
  onToggleInteract: () => void;
}

const EmbedInspector: React.FC<Props> = ({ cfg: rawCfg, setCfg, interacting, onToggleInteract }) => {
  const cfg: WebsiteConfig = readWebsiteConfig(rawCfg);
  const [draft, setDraft] = useState<string>(cfg.url ?? "");

  const validationMsg = validateWebsiteInput(cfg, draft);

  const commitSource = (value: string) => {
    setDraft(value);
    const s = value.trim();
    if (!s) { setCfg({ url: "" }); return; }
    if (cfg.input_type === "embed") {
      const src = extractIframeSrc(s);
      const meta = extractIframeMeta(s);
      // Only the safe src survives; leave stored url untouched on invalid embed.
      if (src) setCfg({ url: src, title: meta.title, allow_fullscreen: cfg.allow_fullscreen || meta.allowFullscreen });
    } else {
      setCfg({ url: s });
    }
  };

  return (
    <Box>
      <TextField
        select size="small" fullWidth label="Input type"
        value={cfg.input_type}
        onChange={(e) => setCfg({ input_type: e.target.value })}
        sx={{ mb: 1.5 }}
      >
        <MenuItem value="url">URL</MenuItem>
        <MenuItem value="embed">iFrame Embed Code</MenuItem>
      </TextField>

      {cfg.input_type === "embed" ? (
        <TextField
          size="small" fullWidth label="iFrame embed code *" multiline minRows={3}
          placeholder='<iframe src="https://…" …></iframe>'
          value={draft}
          onChange={(e) => commitSource(e.target.value)}
          error={!!validationMsg}
          helperText={validationMsg ?? (cfg.url ? `Extracted src: ${cfg.url}` : "Only the safe src is stored — scripts and raw HTML are stripped.")}
          sx={{ mb: 1.5 }}
        />
      ) : (
        <TextField
          size="small" fullWidth label="Embed URL *"
          placeholder="https://example.com/embed/…"
          value={draft}
          onChange={(e) => commitSource(e.target.value)}
          error={!!validationMsg}
          helperText={validationMsg ?? (cfg.url && !isValidWebUrl(cfg.url) ? "Enter a valid https:// URL." : undefined)}
          sx={{ mb: 1.5 }}
        />
      )}

      <TextField
        select size="small" fullWidth label="Refresh interval"
        value={cfg.refresh_interval}
        onChange={(e) => setCfg({ refresh_interval: Number(e.target.value) })}
        sx={{ mb: 1.5 }}
      >
        {REFRESH_INTERVALS.map((r) => (
          <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
        ))}
      </TextField>

      <TextField
        select size="small" fullWidth label="Fit mode"
        value={cfg.fit}
        onChange={(e) => setCfg({ fit: e.target.value })}
        sx={{ mb: 1.5 }}
      >
        {WEBSITE_FITS.map((f) => (
          <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
        ))}
      </TextField>

      <TextField
        size="small" fullWidth type="color" label="Background colour"
        value={cfg.background ?? "#000000"}
        onChange={(e) => setCfg({ background: e.target.value })}
        sx={{ mb: 1.5 }}
      />

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: NAVY, mb: 0.5,
        textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Behaviour
      </Typography>
      <FormControlLabel
        control={<Switch size="small" checked={!!cfg.show_scrollbars}
          onChange={(e) => setCfg({ show_scrollbars: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>Show scrollbars</Typography>}
        sx={{ display: "flex", ml: 0 }}
      />
      <FormControlLabel
        control={<Switch size="small" checked={!!cfg.allow_interaction}
          onChange={(e) => setCfg({ allow_interaction: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>Allow interaction</Typography>}
        sx={{ display: "flex", ml: 0 }}
      />
      <FormControlLabel
        control={<Switch size="small" checked={!!cfg.allow_fullscreen}
          onChange={(e) => setCfg({ allow_fullscreen: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>Allow fullscreen</Typography>}
        sx={{ display: "flex", ml: 0 }}
      />

      <Button
        fullWidth size="small" variant={interacting ? "contained" : "outlined"}
        color={interacting ? "secondary" : "primary"}
        startIcon={<TouchAppIcon />}
        onClick={onToggleInteract}
        sx={{ mt: 1.5 }}
      >
        {interacting ? "Return to editing" : "Interact with content"}
      </Button>
      {interacting && (
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.75 }}>
          The embedded content is now clickable/scrollable on the canvas.
          Dragging and resizing are paused — choose “Return to editing” to move
          or resize the zone again.
        </Typography>
      )}
    </Box>
  );
};

export default EmbedInspector;
